import { inject, injectable } from 'tsyringe';
import { BaseViewModel } from '@/shared/viewmodels/BaseViewModel';
import { GlobalFilterController } from '@/features/global-filters/GlobalFilterController';
import type { AnalyticsFilter } from '@/shared/analytics/AnalyticsTypes';
import { IAdoptionGatewayToken, type AdoptionGateway } from './AdoptionGateway';
import type { AdoptionBreakdownRow, AdoptionScope, AdoptionScore, AdoptionTrend } from './AdoptionTypes';

/**
 * Drives the adoption overview and drivers-&-trends screens: loads the org
 * score, the scored breakdown rows for the grid, and the driver trend series —
 * the latter re-fetched narrowed to a scope when a grid row is clicked.
 */
@injectable()
export class AdoptionViewModel extends BaseViewModel {
    score: AdoptionScore | null = null;
    breakdown: AdoptionBreakdownRow[] = [];
    trend: AdoptionTrend | null = null;
    /** The active drill path (org → dimension value → …); empty means the whole org. */
    scopePath: AdoptionScope[] = [];
    loading = true;
    error = false;

    private mode: 'overview' | 'detail' | null = null;
    private ambientFilters: AnalyticsFilter[] = [];
    private readonly unsubscribe: () => void;

    constructor(
        @inject(IAdoptionGatewayToken) private readonly gateway: AdoptionGateway,
        @inject(GlobalFilterController) globalFilters: GlobalFilterController,
    ) {
        super();
        this.unsubscribe = globalFilters.subscribe(() => {
            void this.reload();
        });
    }

    override dispose(): void {
        this.unsubscribe();
        super.dispose();
    }

    /** Sets the page-scoped assist filters applied on top of the global filters. */
    setAmbientFilters(filters: AnalyticsFilter[]): void {
        this.ambientFilters = filters;
    }

    /** Overview screen: the composite score plus the breakdown feeding the level-distribution card. */
    async loadOverview(): Promise<void> {
        this.mode = 'overview';
        this.loading = true;
        this.error = false;
        this.notifyChange();
        try {
            const [score, breakdown] = await Promise.all([this.gateway.getScore(this.ambientFilters), this.gateway.getBreakdown(this.ambientFilters)]);
            this.score = score;
            this.breakdown = breakdown;
        } catch {
            this.error = true;
        }
        this.loading = false;
        this.notifyChange();
    }

    /** Drivers screen: score (for weights/labels), grid rows, and org-level trend. */
    async loadDetail(): Promise<void> {
        this.mode = 'detail';
        this.loading = true;
        this.error = false;
        this.scopePath = [];
        this.notifyChange();
        try {
            const [score, breakdown, trend] = await Promise.all([
                this.gateway.getScore(this.ambientFilters),
                this.gateway.getBreakdown(this.ambientFilters),
                this.gateway.getTrend(this.ambientFilters, []),
            ]);
            this.score = score;
            this.breakdown = breakdown;
            this.trend = trend;
        } catch {
            this.error = true;
        }
        this.loading = false;
        this.notifyChange();
    }

    private async reload(): Promise<void> {
        if (this.mode === 'overview') await this.loadOverview();
        else if (this.mode === 'detail') await this.loadDetail();
    }

    /** Sets the drill path (empty resets to the whole org) and refetches the scoped trends. */
    async drillTo(path: AdoptionScope[]): Promise<void> {
        this.scopePath = path;
        this.notifyChange();
        this.trend = await this.gateway.getTrend(this.ambientFilters, path);
        this.notifyChange();
    }
}
